import type { MarketSelectorPreset } from '@src/components/MarketSelector/types';
import {
  AggregationIcon,
  BaseSelect,
  FormControlSizes,
  HStack,
  Icon,
  IconButton,
  IconName,
  Stack,
  Text,
  Tooltip,
  useMarketAccountsContext,
  useMarketsContext,
} from '@talos/kyoko';
import { identity } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { UnifiedLiquidityMarketTooltipContent } from '../MarketSelector/UnifiedLiquidityMarketTooltipContent';
import { NavigableControlArrow, NavigableControlRow } from './components/NavigableControlRow';
import { SelectedFieldTooltipContent } from './components/SelectedFieldTooltipContent';
import { MARKET_CARD_CONTROL_TOOLTIP_PROPS } from './marketCardControlTooltipProps';
import type { UnifiedLiquidityMarketTooltipGroup } from './marketCardUnifiedLiquidityTooltip';

export interface MultilegMarketAccountTooltipGroup {
  key: string;
  displayName: string;
  marketAccountNames: string[];
}

export const MarketCardMarketAccountButtonViewMode = {
  Icon: 'icon',
  FullWidth: 'fullWidth',
} as const;
export type MarketCardMarketAccountButtonViewMode =
  (typeof MarketCardMarketAccountButtonViewMode)[keyof typeof MarketCardMarketAccountButtonViewMode];

export function MarketCardMarketAccountButton({
  onClick,
  size = FormControlSizes.Small,
  viewMode = MarketCardMarketAccountButtonViewMode.Icon,
  showAggregationNavigation = true,
  isEditSymbolMode = false,
  label,
  selectedMarketOrAccountNames,
  selectedAggregationID,
  aggregationPresets,
  onChangeAggregation,
  unifiedLiquidityTooltipGroups = [],
  multilegMarketAccountTooltipGroups = [],
}: {
  onClick: () => void;
  size?: FormControlSizes;
  viewMode?: MarketCardMarketAccountButtonViewMode;
  showAggregationNavigation?: boolean;
  isEditSymbolMode?: boolean;
  label: string;
  selectedMarketOrAccountNames: string[];
  selectedAggregationID?: string;
  aggregationPresets: MarketSelectorPreset[];
  onChangeAggregation: (aggregationID: string) => void;
  unifiedLiquidityTooltipGroups?: UnifiedLiquidityMarketTooltipGroup[];
  multilegMarketAccountTooltipGroups?: MultilegMarketAccountTooltipGroup[];
}) {
  const sortedAggregationPresets = useMemo(
    () => aggregationPresets.toSorted((a, b) => a.label.localeCompare(b.label)),
    [aggregationPresets]
  );
  const tooltip = (
    <MarketCardMarketAccountTooltipContent
      selectedMarketOrAccountNames={selectedMarketOrAccountNames}
      unifiedLiquidityTooltipGroups={unifiedLiquidityTooltipGroups}
      multilegMarketAccountTooltipGroups={multilegMarketAccountTooltipGroups}
    />
  );
  const shouldRenderAggregationNavigation = useMemo(() => {
    if (isEditSymbolMode || !showAggregationNavigation || sortedAggregationPresets.length === 0) {
      return false;
    }

    return !(sortedAggregationPresets.length === 1 && sortedAggregationPresets[0].id === selectedAggregationID);
  }, [isEditSymbolMode, selectedAggregationID, showAggregationNavigation, sortedAggregationPresets]);
  const currentAggregationIndex = useMemo(
    () => sortedAggregationPresets.findIndex(aggregation => aggregation.id === selectedAggregationID),
    [selectedAggregationID, sortedAggregationPresets]
  );

  const handlePreviousAggregation = useCallback(() => {
    if (!sortedAggregationPresets.length) {
      return;
    }
    let previousIndex = currentAggregationIndex - 1;
    if (currentAggregationIndex === -1 || previousIndex < 0) {
      previousIndex = sortedAggregationPresets.length - 1;
    }
    onChangeAggregation(sortedAggregationPresets[previousIndex].id);
  }, [currentAggregationIndex, onChangeAggregation, sortedAggregationPresets]);

  const handleNextAggregation = useCallback(() => {
    if (!sortedAggregationPresets.length) {
      return;
    }
    let nextIndex = currentAggregationIndex + 1;
    if (currentAggregationIndex === -1 || nextIndex > sortedAggregationPresets.length - 1) {
      nextIndex = 0;
    }
    onChangeAggregation(sortedAggregationPresets[nextIndex].id);
  }, [currentAggregationIndex, onChangeAggregation, sortedAggregationPresets]);

  if (viewMode === MarketCardMarketAccountButtonViewMode.FullWidth) {
    const content = (
      <MarketSelectorBaseSelect
        value={label}
        getLabel={identity}
        customButtonLayout={props => (
          <MarketSelectorButtonLayout item={props.item} isEditSymbolMode={isEditSymbolMode} />
        )}
        isDropdownOpened={false}
        onClick={onClick}
        showChevron={false}
        showTitle={false}
        centered
        size={size}
        width="100%"
        dataTestID="market-card-market-acct-button"
      />
    );

    return (
      <NavigableControlRow
        left={
          shouldRenderAggregationNavigation ? (
            <NavigableControlArrow
              direction="previous"
              size={size}
              className="aggregation-prev"
              dataTestID="aggregation-prev"
              tooltip="Previous Favorite Aggregation"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                handlePreviousAggregation();
              }}
            />
          ) : undefined
        }
        right={
          shouldRenderAggregationNavigation ? (
            <NavigableControlArrow
              direction="next"
              size={size}
              className="aggregation-next"
              dataTestID="aggregation-next"
              tooltip="Next Favorite Aggregation"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                handleNextAggregation();
              }}
            />
          ) : undefined
        }
      >
        {isEditSymbolMode ? (
          content
        ) : (
          <Tooltip
            tooltip={tooltip}
            isSmall={unifiedLiquidityTooltipGroups.length ? false : undefined}
            {...MARKET_CARD_CONTROL_TOOLTIP_PROPS}
          >
            {content}
          </Tooltip>
        )}
      </NavigableControlRow>
    );
  }

  if (isEditSymbolMode) {
    return (
      <MarketCardMarketAccountButtonElement
        aria-label="Edit Symbol"
        data-testid="market-card-market-acct-button"
        icon={IconName.Pencil}
        onClick={onClick}
        size={size}
      />
    );
  }

  return (
    <NavigableControlRow
      left={
        shouldRenderAggregationNavigation ? (
          <NavigableControlArrow
            direction="previous"
            size={size}
            className="aggregation-prev"
            dataTestID="aggregation-prev"
            tooltip="Previous Favorite Aggregation"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handlePreviousAggregation();
            }}
          />
        ) : undefined
      }
      right={
        shouldRenderAggregationNavigation ? (
          <NavigableControlArrow
            direction="next"
            size={size}
            className="aggregation-next"
            dataTestID="aggregation-next"
            tooltip="Next Favorite Aggregation"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleNextAggregation();
            }}
          />
        ) : undefined
      }
    >
      <Tooltip
        tooltip={tooltip}
        isSmall={unifiedLiquidityTooltipGroups.length ? false : undefined}
        {...MARKET_CARD_CONTROL_TOOLTIP_PROPS}
      >
        <MarketCardMarketAccountButtonElement
          aria-label="Open Market Selector"
          data-testid="market-card-market-acct-button"
          icon={IconName.Scale}
          color="colors.purple.lighten"
          onClick={onClick}
          size={size}
        />
      </Tooltip>
    </NavigableControlRow>
  );
}

function MarketCardMarketAccountTooltipContent({
  selectedMarketOrAccountNames,
  unifiedLiquidityTooltipGroups,
  multilegMarketAccountTooltipGroups,
}: {
  selectedMarketOrAccountNames: string[];
  unifiedLiquidityTooltipGroups: UnifiedLiquidityMarketTooltipGroup[];
  multilegMarketAccountTooltipGroups: MultilegMarketAccountTooltipGroup[];
}) {
  const { marketDisplayNameByName } = useMarketsContext();
  const { marketAccountDisplayNameByName } = useMarketAccountsContext();
  const lines = useMemo(() => {
    return selectedMarketOrAccountNames
      .map(selectionName =>
        getSelectionDisplayName(selectionName, marketAccountDisplayNameByName, marketDisplayNameByName)
      )
      .toSorted((a, b) => a.localeCompare(b));
  }, [marketAccountDisplayNameByName, marketDisplayNameByName, selectedMarketOrAccountNames]);

  return multilegMarketAccountTooltipGroups.length ? (
    <MarketCardMultilegMarketAccountTooltipContent groups={multilegMarketAccountTooltipGroups} />
  ) : unifiedLiquidityTooltipGroups.length ? (
    <MarketCardUnifiedLiquidityTooltipContent groups={unifiedLiquidityTooltipGroups} />
  ) : (
    <SelectedFieldTooltipContent
      title="Selected Markets and Accounts"
      lines={lines}
      emptyMessage="No selected markets or accounts"
    />
  );
}

function MarketCardMultilegMarketAccountTooltipContent({ groups }: { groups: MultilegMarketAccountTooltipGroup[] }) {
  const { marketAccountDisplayNameByName } = useMarketAccountsContext();

  return (
    <Stack gap="spacing8" alignItems="stretch" data-testid="market-card-multileg-market-account-tooltip">
      <Text fontWeight="500" color="colorTextImportant">
        Selected Markets and Accounts
      </Text>
      <Stack gap="spacing8">
        {groups.map(group => (
          <Stack key={group.key} gap="spacing4">
            <Text fontWeight="500" color="colorTextImportant">
              {group.displayName}
            </Text>
            {group.marketAccountNames.length > 0 ? (
              group.marketAccountNames.map(marketAccountName => (
                <Text key={marketAccountName} color="colorTextHighlight">
                  {marketAccountDisplayNameByName.get(marketAccountName) ?? marketAccountName}
                </Text>
              ))
            ) : (
              <Text color="colorTextHighlight">All Markets</Text>
            )}
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}

function MarketCardUnifiedLiquidityTooltipContent({ groups }: { groups: UnifiedLiquidityMarketTooltipGroup[] }) {
  return (
    <UnifiedLiquidityMarketTooltipContent
      groups={groups}
      dataTestId="market-card-unified-liquidity-tooltip"
      statusTestIdPrefix="market-card-unified-liquidity-tooltip-status"
    />
  );
}

function getSelectionDisplayName(
  selectionName: string,
  marketAccountDisplayNameByName: Map<string, string>,
  marketDisplayNameByName: Map<string, string>
) {
  return (
    marketAccountDisplayNameByName.get(selectionName) ?? marketDisplayNameByName.get(selectionName) ?? selectionName
  );
}

export const MarketCardMarketAccountButtonElement = styled(IconButton)``;
const MarketSelectorBaseSelect = styled(BaseSelect<string>)`
  width: 100%;
`;
const MarketSelectorButtonLayout = ({
  item,
  isEditSymbolMode,
}: {
  item: string | undefined;
  isEditSymbolMode: boolean;
}) => {
  return (
    <MarketSelectorButtonContent gap="spacing4">
      {isEditSymbolMode ? <Icon icon={IconName.Pencil} /> : <AggregationIcon />}
      <MarketSelectorLabel>{item}</MarketSelectorLabel>
    </MarketSelectorButtonContent>
  );
};
const MarketSelectorButtonContent = styled(HStack)`
  min-width: 0;
  width: 100%;
  justify-content: center;
`;
const MarketSelectorLabel = styled(Text)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
